import { useEffect, useRef } from "react";
import { INTRO_BRAIN_TRACKS, INTRO_DARK, INTRO_GOLD, INTRO_PLANE_CENTER, INTRO_RIBBON, INTRO_VIEWBOX } from "./introArt";
import "../../styles/introFlow.css";

const DURATION = 4000;
const TAIL = 7;
const TAIL_STEP = 3.6;

/**
 * Animazione di ingresso disegnata sull'illustrazione dell'officina:
 * il tratto nero (testa e cervello) e quello dorato (nastro e aereo) si disegnano,
 * mentre le particelle scorrono dentro i canali del cervello e confluiscono nell'aereo.
 */
export function IntroFlow({ onDone, caption = true }) {
  const rootRef = useRef(null);
  const canvasRef = useRef(null);
  const doneRef = useRef(false);
  const onDoneRef = useRef(onDone);

  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);

  const finish = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (onDoneRef.current) onDoneRef.current();
  };

  useEffect(() => {
    const timer = setTimeout(finish, DURATION);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    const canvas = canvasRef.current;
    if (!root || !canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return undefined;

    const tracks = Array.from(root.querySelectorAll("[data-track]")).map((path) => {
      const total = path.getTotalLength();
      const oneWay = total / 2;
      return { path, oneWay };
    });
    const ribbonPath = root.querySelector("[data-ribbon]");
    const ribbonTotal = ribbonPath ? ribbonPath.getTotalLength() / 2 : 0;
    const ribbonReversed = ribbonPath
      ? ribbonPath.getPointAtLength(0).y < ribbonPath.getPointAtLength(ribbonTotal).y
      : false;

    const sparks = tracks.flatMap((track, index) => (
      [0, 1, 2].filter((slot) => slot < 2 || track.oneWay > 110).map((slot) => ({
        track,
        length: Math.random() * track.oneWay,
        speed: 78 + Math.random() * 62,
        size: 0.8 + Math.random() * 0.75,
        delay: 560 + index * 54 + slot * 320 + Math.random() * 420,
      }))
    ));
    const flyers = [];
    let nextFlyer = 1360;
    let planePulse = 0;

    let box = { left: 0, top: 0, scale: 1 };
    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(bounds.width * ratio);
      canvas.height = Math.round(bounds.height * ratio);
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      const scale = Math.min(bounds.width / INTRO_VIEWBOX.width, bounds.height / INTRO_VIEWBOX.height);
      box = {
        scale,
        left: (bounds.width - INTRO_VIEWBOX.width * scale) / 2,
        top: (bounds.height - INTRO_VIEWBOX.height * scale) / 2,
      };
    };
    resize();
    window.addEventListener("resize", resize);

    const project = (point) => ({ x: box.left + point.x * box.scale, y: box.top + point.y * box.scale });

    const glow = (point, radius, alpha, core = "255, 251, 236") => {
      const gradient = ctx.createRadialGradient(point.x, point.y, 0, point.x, point.y, radius);
      gradient.addColorStop(0, `rgba(${core}, ${alpha})`);
      gradient.addColorStop(0.34, `rgba(233, 191, 92, ${alpha * 0.7})`);
      gradient.addColorStop(1, "rgba(201, 151, 31, 0)");
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
      ctx.fill();
    };

    const drawComet = (path, length, limit, alpha, size) => {
      const head = project(path.getPointAtLength(length));
      ctx.save();
      ctx.lineCap = "round";
      for (let index = 1; index <= TAIL; index += 1) {
        const at = length - index * TAIL_STEP;
        if (at < 0) break;
        const from = project(path.getPointAtLength(at));
        const to = project(path.getPointAtLength(Math.min(at + TAIL_STEP, limit)));
        const strength = (1 - index / TAIL) ** 1.4;
        ctx.strokeStyle = `rgba(226, 183, 84, ${alpha * strength * 0.75})`;
        ctx.lineWidth = Math.max(box.scale * 2.6 * size * strength, 0.7);
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();
      }
      glow(head, Math.max(box.scale * 9 * size, 3.4), alpha);
      ctx.restore();
    };

    const start = performance.now();
    let previous = start;
    let frame = requestAnimationFrame(function render(time) {
      const now = time - start;
      const delta = Math.min(time - previous, 48);
      previous = time;
      const bounds = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, bounds.width, bounds.height);
      const fade = now < 2950 ? 1 : Math.max(1 - (now - 2950) / 550, 0);

      sparks.forEach((spark) => {
        if (now < spark.delay) return;
        spark.length += (spark.speed * delta) / 1000;
        if (spark.length > spark.track.oneWay) {
          spark.length = 0;
          spark.delay = now + 200 + Math.random() * 900;
          return;
        }
        const progress = spark.length / spark.track.oneWay;
        const alpha = Math.sin(Math.PI * Math.min(progress * 1.15, 1)) * 0.95 * fade;
        if (alpha > 0.02) drawComet(spark.track.path, spark.length, spark.track.oneWay, alpha, spark.size);
      });

      if (ribbonPath && now > nextFlyer) {
        flyers.push({ length: 0, speed: 400 + Math.random() * 150, size: 1.1 + Math.random() * 0.5 });
        nextFlyer = now + 300 + Math.random() * 240;
      }
      for (let index = flyers.length - 1; index >= 0; index -= 1) {
        const flyer = flyers[index];
        flyer.length += (flyer.speed * delta) / 1000;
        if (flyer.length >= ribbonTotal) {
          flyers.splice(index, 1);
          planePulse = 1;
          continue;
        }
        const at = ribbonReversed ? ribbonTotal - flyer.length : flyer.length;
        const alpha = Math.min(flyer.length / 90, 1) * fade;
        drawComet(ribbonPath, at, ribbonTotal, alpha, flyer.size);
      }

      if (planePulse > 0) {
        planePulse = Math.max(planePulse - delta / 520, 0);
        glow(project(INTRO_PLANE_CENTER), Math.max(box.scale * 96 * planePulse, 6), planePulse * 0.5 * fade);
      }

      frame = requestAnimationFrame(render);
    });

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", resize);
    };
  }, []);

  const viewBox = `0 0 ${INTRO_VIEWBOX.width} ${INTRO_VIEWBOX.height}`;

  return (
    <div className="intro-flow" ref={rootRef} data-testid="intro-flow">
      <div className="intro-flow-stage">
        <svg className="intro-flow-art" viewBox={viewBox} preserveAspectRatio="xMidYMid meet" aria-hidden="true">
          <defs>
            <linearGradient id="introEdge" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#ffffff" />
              <stop offset="0.94" stopColor="#ffffff" />
              <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
            </linearGradient>
            <mask id="introMaskDark" maskUnits="userSpaceOnUse" x="-2000" y="-2000" width="5000" height="5000">
              <g transform={`rotate(-16 ${INTRO_VIEWBOX.width / 2} ${INTRO_VIEWBOX.height / 2})`}>
                <rect className="intro-wipe intro-wipe-dark" x="-2600" y="-1400" width="3200" height="4000" fill="url(#introEdge)" />
              </g>
            </mask>
            <mask id="introMaskGold" maskUnits="userSpaceOnUse" x="-2000" y="-2000" width="5000" height="5000">
              <g transform={`rotate(-52 ${INTRO_VIEWBOX.width / 2} ${INTRO_VIEWBOX.height / 2})`}>
                <rect className="intro-wipe intro-wipe-gold" x="-2600" y="-1600" width="3200" height="4400" fill="url(#introEdge)" />
              </g>
            </mask>
          </defs>
          <g mask="url(#introMaskDark)">
            <path d={INTRO_DARK} fill="#2f2e29" />
          </g>
          <g mask="url(#introMaskGold)">
            <path d={INTRO_GOLD} fill="#c9971f" />
          </g>
          <g className="intro-flow-tracks">
            {INTRO_BRAIN_TRACKS.map((track, index) => <path key={index} d={track} data-track="" fill="none" stroke="none" />)}
            <path d={INTRO_RIBBON} data-ribbon="" fill="none" stroke="none" />
          </g>
        </svg>
        <canvas className="intro-flow-sparks" ref={canvasRef} aria-hidden="true" />
      </div>

      {caption && (
        <p className="intro-flow-caption" data-testid="intro-flow-caption">
          <span className="intro-word intro-word-dark">Dall’</span>
          <span className="intro-word intro-word-gold">idea</span>
          <span className="intro-word intro-word-dark">al</span>
          <span className="intro-word intro-word-gold-soft">flusso di officina</span>
        </p>
      )}

      <button type="button" className="intro-flow-skip" onClick={finish} data-testid="intro-flow-skip">Salta</button>
      <div className="intro-flow-veil" aria-hidden="true" />
    </div>
  );
}
